import {
  AnyVariables,
  Client,
  CombinedError,
  TypedDocumentNode,
} from '@urql/core';
import { filter, reduce, map as mapA } from 'fp-ts/Array';
import { matchW } from 'fp-ts/boolean';
import { Lazy, pipe } from 'fp-ts/function';
import { fromPredicate, getOrElse, map, none, some } from 'fp-ts/Option';
import { toEntries } from 'fp-ts/Record';
import {
  chain,
  fromOption,
  left,
  right,
  TaskEither,
  tryCatch,
} from 'fp-ts/TaskEither';
import { Lens, Optional } from 'monocle-ts';
import { prop } from 'monocle-ts/lib/Optional';
import { Collection } from '../types/contentful';
import { FAQQuestionPreview } from '@oresundsbron/validators';

const isRecord = (e: unknown): e is Record<string, unknown> =>
  e !== undefined &&
  e !== null &&
  Object.prototype.toString.call(e) === '[object Object]' &&
  !Array.isArray(e);

const isCollection = <T>(e: unknown): e is Collection<T> =>
  isRecord(e) &&
  (!!(e as Collection<T>).items || (e as Collection<T>).total !== undefined);

export const listItems = <T>() =>
  Lens.fromNullableProp<Collection<T>>()('items', []);

export const removeCollectionLayers = <T>(entry: T): T =>
  pipe(
    entry,
    fromPredicate(isRecord),
    map(toEntries),
    map(
      reduce(entry as T, (res, [key, val]) =>
        pipe(
          val,
          fromPredicate(isCollection),
          map(listItems().get),
          map(filter(Boolean)),
          map(mapA(removeCollectionLayers)),
          map((items) => ({ ...res, [key]: items })),
          getOrElse(() => ({ ...res, [key]: removeCollectionLayers(val) }))
        )
      )
    ),
    getOrElse(() => entry)
  );

export const request =
  (client: Client) =>
  <Data, Variables extends AnyVariables = AnyVariables>(
    document: TypedDocumentNode<Data, Variables>,
    variables?: Variables
  ): TaskEither<Error | CombinedError, Data> =>
    pipe(
      tryCatch(
        () => {
          return client
            .query<Data, Variables>(document, variables || ({} as Variables))
            .toPromise();
        },
        (e) => {
          typeof window === 'undefined' &&
            console.error(`Contentful request failed`, e);

          return e as Error;
        }
      ),
      chain((result) =>
        pipe(
          !!result.error && !result.data,
          matchW(
            () => right(result.data as Data),
            () => left(result.error as CombinedError)
          )
        )
      )
    );

export type ContentfulRequest = ReturnType<typeof request>;

export const requestV2 =
  (client: Client) =>
  async <Data, Variables extends AnyVariables = AnyVariables>(
    document: TypedDocumentNode<Data, Variables>,
    variables?: Variables
  ): Promise<Data> => {
    try {
      const result = await client
        .query<Data, Variables>(document, variables || ({} as Variables))
        .toPromise();

      if (result.error && !result.data) {
        throw result.error;
      }

      return result.data as Data;
    } catch (e) {
      if (typeof window === 'undefined') {
        console.error('Contentful request failed', e);
      }
      throw e;
    }
  };

export type ContentfulRequestV2 = ReturnType<typeof requestV2>;

export const getOption = <T>(arr: T[]) =>
  arr.length === 0 ? none : some(arr[0]);

export const set =
  <T>(p: T) =>
  (ps: T[]) =>
    ps.length === 0 ? [] : [p, ...ps.slice(1)];

export const head = <T>() => new Optional<Array<T>, T>(getOption, set);
const headFromItems = <T>() => listItems<T>().composeOptional(head<T>());

export const removeItemsCollectionLayers = <T>() =>
  listItems<T>().modify((list) => list.map(removeCollectionLayers));

export const getHead =
  <T, L>(onNone: Lazy<L>) =>
  (collection: Collection<T>) =>
    pipe(collection, headFromItems<T>().getOption, fromOption(onNone));

export const getCollection =
  <T>(k: string) =>
  <X extends { [key: string]: Collection<T> }, K extends keyof X>(x: X) =>
    Lens.fromNullableProp<X>()(
      k as K,
      {
        total: 0,
        items: [],
      } as unknown as X[K]
    ).get(x);

const cGroupCollectionLens = Lens.fromPath<FAQQuestionPreview>()([
  'linkedFrom',
  'cGroupCollection',
]);

export const faqParentSlug = pipe(
  cGroupCollectionLens.composeOptional(head()),
  prop('slug')
);
